import { enUS, fr, type Locale } from 'date-fns/locale'
import { queryClient } from '@/app/queryClient'
import { brandKeys } from '@/shared/api/brandQueries'
import { i18n } from '@/shared/i18n/i18n'
import {
  timezoneFromSnapshot,
  type BrandAuthenticatedSnapshot,
} from '@/shared/schemas/brand.schema'

/**
 * BCP-47 locale tag derived from the active i18n language (`fr_FR` / `en_US`),
 * for `Date.prototype.toLocaleDateString` / `toLocaleString`. Avoids hardcoding
 * `'fr-FR'` regardless of the selected language (AC-F03).
 */
export function localeTag(): string {
  const language = i18n.language ?? 'fr_FR'

  return language.startsWith('en') ? 'en-US' : 'fr-FR'
}

/** date-fns `Locale` matching the active i18n language, for `format(date, pattern, { locale })`. */
export function dateFnsLocale(): Locale {
  const language = i18n.language ?? 'fr_FR'

  return language.startsWith('en') ? enUS : fr
}

/**
 * Display timezone: explicit snapshot → brand cache → `VITE_APP_TIMEZONE` → Europe/Paris.
 * Storage/API remain UTC/ISO; this only affects portal formatting.
 */
export function appTimeZone(snapshot?: BrandAuthenticatedSnapshot | null): string {
  const fromArg = timezoneFromSnapshot(snapshot ?? undefined)
  if (fromArg !== null) {
    return fromArg
  }
  const cached = queryClient.getQueryData<BrandAuthenticatedSnapshot>(brandKeys.authenticatedSnapshot())
  const fromCache = timezoneFromSnapshot(cached)
  if (fromCache !== null) {
    return fromCache
  }
  const fromEnv = import.meta.env.VITE_APP_TIMEZONE
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return fromEnv.trim()
  }

  return 'Europe/Paris'
}

const FR_DATE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}

const FR_DATETIME: Intl.DateTimeFormatOptions = {
  ...FR_DATE,
  hour: '2-digit',
  minute: '2-digit',
}

const FR_TIME: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}

/** Format an ISO `yyyy-MM-dd` (or datetime) for display - FR → `29/07/2026`. */
export function formatIsoDateDisplay(iso: string, snapshot?: BrandAuthenticatedSnapshot | null): string {
  if (iso === '') {
    return ''
  }
  const d = iso.includes('T') ? new Date(iso) : new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) {
    return iso
  }

  return d.toLocaleDateString(localeTag(), { ...FR_DATE, timeZone: appTimeZone(snapshot) })
}

/** Format an ISO datetime for display - FR → `29/07/2026 14:30`. */
export function formatIsoDateTimeDisplay(iso: string, snapshot?: BrandAuthenticatedSnapshot | null): string {
  if (iso === '') {
    return ''
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }

  return d.toLocaleString(localeTag(), { ...FR_DATETIME, timeZone: appTimeZone(snapshot) })
}

/** Time-only portion of an ISO datetime - FR → `14:30`. Pairs with `formatIsoDateDisplay` for
 * "le DD/MM/YYYY à HH:mm" copy where the date/time need separate i18n placeholders. */
export function formatIsoTimeDisplay(iso: string, snapshot?: BrandAuthenticatedSnapshot | null): string {
  if (iso === '') {
    return ''
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }

  return d.toLocaleTimeString(localeTag(), { ...FR_TIME, timeZone: appTimeZone(snapshot) })
}
